import { RERUN_VIEWER_VERSION } from "./types";
import { isMobileOperatingSystem } from "../utils/device";
import type {
  AuthUser,
  CreateSessionResponse,
  PlaybackResponse,
  SessionManifest,
  SessionRecord,
  TokenResponse,
  Annotation,
  AutomaticLabel,
  Issue,
  Invitation,
  Job,
  Member,
  Organization,
  PageRequest,
  PageResult,
  Project,
  Review,
  Task,
} from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<TokenResponse> | null = null;

  async login(username: string, password: string): Promise<AuthUser> {
    const token = await this.publicJson<TokenResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    this.acceptToken(token);
    return token.user ?? { username };
  }

  async restoreSession(): Promise<AuthUser> {
    const token = await this.refresh();
    return token.user ?? { username: "当前用户" };
  }

  async logout(): Promise<void> {
    try {
      await this.request<void>("/api/auth/logout", { method: "POST" });
    } finally {
      this.accessToken = null;
    }
  }

  listSessions(): Promise<SessionRecord[]> { return this.request("/api/sessions"); }
  getSession(sessionId: string): Promise<SessionRecord> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}`);
  }
  listAutomaticLabels(sessionId: string): Promise<AutomaticLabel[]> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/automatic-labels`);
  }
  createSession(rawManifest: string, projectId: string): Promise<CreateSessionResponse> {
    return this.request(`/api/sessions?project_id=${encodeURIComponent(projectId)}`, { method: "POST", body: rawManifest });
  }
  async playback(sessionId: string): Promise<PlaybackResponse> {
    const response = await this.request<PlaybackWireResponse>(
      `/api/sessions/${encodeURIComponent(sessionId)}/playback${isMobileOperatingSystem() ? "?mobile=true" : ""}`,
    );
    return { ...response, viewer_version: response.viewer_version || RERUN_VIEWER_VERSION };
  }
  deleteSession(sessionId: string, confirmation: string): Promise<void> {
    const id = encodeURIComponent(sessionId);
    return this.request(`/api/sessions/${id}?confirm=${encodeURIComponent(confirmation)}`, {
      method: "DELETE",
    });
  }
  finalize(sessionId: string): Promise<SessionRecord> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/finalize`, { method: "POST" });
  }
  signPart(sessionId: string, path: string, partNumber: number): Promise<{ url: string }> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/uploads/sign-part`, {
      method: "POST", body: JSON.stringify({ path, part_number: partNumber }),
    });
  }
  signPut(sessionId: string, path: string): Promise<{ url: string }> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/uploads/sign-put`, {
      method: "POST", body: JSON.stringify({ path }),
    });
  }
  completeMultipart(sessionId: string, path: string, parts: Array<{ part_number: number; etag: string }>): Promise<void> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/uploads/complete`, {
      method: "POST", body: JSON.stringify({ path, parts }),
    });
  }
  markUploaded(sessionId: string, path: string): Promise<void> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/uploads/mark-uploaded`, {
      method: "POST", body: JSON.stringify({ path }),
    });
  }

  uploadStatus(sessionId: string): Promise<{ session: SessionRecord; files: Array<{ path: string; completed: boolean }>; strategy: string; part_size_bytes: number }> {
    return this.request(`/api/sessions/${encodeURIComponent(sessionId)}/uploads`);
  }
  listOrganizations(): Promise<Organization[]> { return this.request("/api/organizations"); }
  createOrganization(slug: string, name: string): Promise<Organization> {
    return this.request("/api/organizations", { method: "POST", body: JSON.stringify({ slug, name }) });
  }
  listMembers(organizationId: string): Promise<Member[]> { return this.request(`/api/organizations/${organizationId}/members`); }
  listInvitations(organizationId: string): Promise<Invitation[]> { return this.request(`/api/organizations/${organizationId}/invitations`); }
  createInvitation(organizationId: string, body: Record<string, string | number>): Promise<Invitation> {
    return this.request(`/api/organizations/${organizationId}/invitations`, { method: "POST", body: JSON.stringify(body) });
  }
  acceptInvitation(token: string, password: string, displayName: string): Promise<Member> {
    return this.request("/api/invitations/accept", { method: "POST", body: JSON.stringify({ token, password, display_name: displayName }) });
  }
  revokeInvitation(organizationId: string, invitationId: string): Promise<Invitation> {
    return this.request(`/api/organizations/${organizationId}/invitations/${invitationId}/revoke`, { method: "POST" });
  }
  listProjects(organizationId: string, page: PageRequest): Promise<PageResult<Project>> { return this.request(`/api/organizations/${organizationId}/projects${pageQuery(page)}`); }
  listOrganizationTasks(organizationId: string, page: PageRequest): Promise<PageResult<Task>> { return this.request(`/api/organizations/${organizationId}/tasks${pageQuery(page)}`); }
  listOrganizationJobs(organizationId: string, page: PageRequest): Promise<PageResult<Job>> { return this.request(`/api/organizations/${organizationId}/jobs${pageQuery(page)}`); }
  getProject(projectId: string): Promise<Project> { return this.request(`/api/projects/${projectId}`); }
  updateLabelSchema(projectId: string, schemaVersion: number, labelSchema: Project["label_schema"]): Promise<Project> {
    return this.request(`/api/projects/${projectId}/label-schema`, {
      method: "PUT", body: JSON.stringify({ schema_version: schemaVersion, label_schema: labelSchema }),
    });
  }
  getSessionScope(sessionId: string): Promise<{ organization_id: string; project_id: string }> { return this.request(`/api/sessions/${sessionId}/scope`); }
  createProject(organizationId: string, body: unknown): Promise<Project> {
    return this.request(`/api/organizations/${organizationId}/projects`, { method: "POST", body: JSON.stringify(body) });
  }
  listProjectSessions(projectId: string): Promise<SessionRecord[]> { return this.request(`/api/projects/${projectId}/sessions`); }
  listTasks(projectId: string, page: PageRequest): Promise<PageResult<Task>> { return this.request(`/api/projects/${projectId}/tasks${pageQuery(page)}`); }
  listProjectJobs(projectId: string, page: PageRequest): Promise<PageResult<Job>> { return this.request(`/api/projects/${projectId}/jobs${pageQuery(page)}`); }
  getTask(taskId: string): Promise<Task> { return this.request(`/api/tasks/${taskId}`); }
  createTask(projectId: string, name: string, sessionId: string): Promise<Task> {
    return this.request(`/api/projects/${projectId}/tasks`, { method: "POST", body: JSON.stringify({ name, session_id: sessionId }) });
  }
  listJobs(taskId: string, page: PageRequest): Promise<PageResult<Job>> { return this.request(`/api/tasks/${taskId}/jobs${pageQuery(page)}`); }
  getJob(jobId: string): Promise<Job> { return this.request(`/api/jobs/${jobId}`); }
  createJob(taskId: string, assigneeId: string, timeline: string, start: string, end: string): Promise<Job> {
    return this.request(`/api/tasks/${taskId}/jobs`, { method: "POST", body: JSON.stringify({ assignee_id: assigneeId, timeline, start_time_ns: start, end_time_ns: end }) });
  }
  submitJob(jobId: string, version: number): Promise<Job> {
    return this.request(`/api/jobs/${jobId}/submit`, { method: "POST", body: JSON.stringify({ version }) });
  }
  listAnnotations(jobId: string): Promise<Annotation[]> { return this.request(`/api/jobs/${jobId}/annotations`); }
  createAnnotation(jobId: string, body: unknown): Promise<Annotation> {
    return this.request(`/api/jobs/${jobId}/annotations`, { method: "POST", body: JSON.stringify(body) });
  }
  updateAnnotation(annotationId: string, body: unknown): Promise<Annotation> {
    return this.request(`/api/annotations/${annotationId}`, { method: "PUT", body: JSON.stringify(body) });
  }
  deleteAnnotation(annotationId: string, version: number): Promise<void> {
    return this.request(`/api/annotations/${annotationId}?version=${version}`, { method: "DELETE" });
  }
  listReviews(jobId: string): Promise<Review[]> { return this.request(`/api/jobs/${jobId}/reviews`); }
  reviewJob(jobId: string, verdict: string, comment: string, version: number): Promise<Review> {
    return this.request(`/api/jobs/${jobId}/reviews`, { method: "POST", body: JSON.stringify({ verdict, comment, version }) });
  }
  listIssues(jobId: string): Promise<Issue[]> { return this.request(`/api/jobs/${jobId}/issues`); }
  createIssue(jobId: string, body: string): Promise<Issue> {
    return this.request(`/api/jobs/${jobId}/issues`, { method: "POST", body: JSON.stringify({ body }) });
  }

  async authorizedUpload(url: string, file: Blob, onProgress: (loaded: number) => void): Promise<void> {
    let response = await xhrUpload(this.resolve(url), file, this.accessToken, onProgress);
    if (response.status === 401) {
      await this.refresh();
      response = await xhrUpload(this.resolve(url), file, this.accessToken, onProgress);
    }
    assertXhrOk(response);
  }

  async signedUpload(url: string, file: Blob, onProgress: (loaded: number) => void): Promise<string> {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && isLocalhost(parsed.hostname))) {
      throw new ApiError("上传签名 URL 必须使用 HTTPS", 0);
    }
    const response = await xhrUpload(parsed.toString(), file, null, onProgress);
    assertXhrOk(response);
    if (!response.etag) throw new ApiError("OSS 未返回 ETag，请检查 CORS 暴露响应头配置", 0);
    return response.etag.replaceAll('"', "");
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response = await this.fetchAuthorized(path, init);
    if (response.status === 401 && path !== "/api/auth/refresh") {
      await this.refresh();
      response = await this.fetchAuthorized(path, init);
    }
    return parseResponse<T>(response);
  }

  private fetchAuthorized(path: string, init: RequestInit): Promise<Response> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    if (this.accessToken) headers.set("Authorization", `Bearer ${this.accessToken}`);
    return fetch(this.resolve(path), { ...init, headers, credentials: "include" });
  }

  private async refresh(): Promise<TokenResponse> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.publicJson<TokenResponse>("/api/auth/refresh", { method: "POST" })
        .then((token) => { this.acceptToken(token); return token; })
        .finally(() => { this.refreshPromise = null; });
    }
    return this.refreshPromise;
  }

  private async publicJson<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    const response = await fetch(this.resolve(path), { ...init, headers, credentials: "include" });
    return parseResponse<T>(response);
  }

  private acceptToken(token: TokenResponse): void {
    if (!token.access_token) throw new ApiError("认证服务未返回 access_token", 500);
    this.accessToken = token.access_token;
  }

  private resolve(path: string): string {
    if (/^https?:\/\//.test(path)) return path;
    return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  }
}

interface XhrResult { status: number; body: string; etag: string | null }
type PlaybackWireResponse = Omit<PlaybackResponse, "viewer_version"> & { viewer_version?: string | null };

function pageQuery(page: PageRequest): string {
  const params = new URLSearchParams({ page: String(page.page), page_size: String(page.page_size) });
  if (page.query?.trim()) params.set("query", page.query.trim());
  if (page.status && page.status !== "all") params.set("status", page.status);
  if (page.sort) params.set("sort", page.sort);
  return `?${params.toString()}`;
}

function xhrUpload(url: string, file: Blob, token: string | null, onProgress: (loaded: number) => void): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText, etag: xhr.getResponseHeader("ETag") });
    xhr.onerror = () => reject(new ApiError("网络中断，上传可稍后继续", 0));
    xhr.onabort = () => reject(new ApiError("上传已取消", 0));
    xhr.send(file);
  });
}

function assertXhrOk(response: XhrResult): void {
  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(extractDetail(safeJson(response.body)) || `上传失败（HTTP ${response.status}）`, response.status);
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
  const text = await response.text();
  const detail = safeJson(text);
  throw new ApiError(extractDetail(detail) || `请求失败（HTTP ${response.status}）`, response.status, detail);
}

function safeJson(value: string): unknown {
  try { return JSON.parse(value); } catch { return value; }
}

function extractDetail(value: unknown): string {
  if (typeof value === "string") return value.slice(0, 500);
  if (value && typeof value === "object" && "detail" in value) {
    const detail = (value as { detail: unknown }).detail;
    return typeof detail === "string" ? detail.slice(0, 500) : "请求参数不正确";
  }
  return "";
}

function isLocalhost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

export const apiClient = new ApiClient();
